import React, { useEffect, useState } from "react";
import { Award, Calendar, Save, Search } from "lucide-react";
import { api } from "@/lib/api";

function toDateInput(iso) {
  if (!iso) return "";
  return iso.slice(0, 10);
}

export default function AdminCertificates() {
  const [certs, setCerts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ started_at: "", issued_at: "" });
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/certificates").then((r) => setCerts(r.data));

  useEffect(() => {
    load();
  }, []);

  const openEdit = (c) => {
    setEditing(c);
    setForm({ started_at: toDateInput(c.started_at), issued_at: toDateInput(c.issued_at) });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/certificates/${editing.certificate_id}/dates`, {
        started_at: new Date(form.started_at).toISOString(),
        issued_at: new Date(form.issued_at).toISOString(),
      });
      setEditing(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const filtered = certs.filter((c) => {
    const q = query.toLowerCase();
    return (
      c.student_name?.toLowerCase().includes(q) ||
      c.course_title?.toLowerCase().includes(q) ||
      c.certificate_id?.toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl">Sertifikalar</h1>
          <p className="text-white/50 text-sm mt-1">
            Grup bazlı eğitimlerde sertifika tarihlerini (başlangıç/bitiş) burada elle düzenleyebilirsiniz —
            aksi halde tarihler öğrencinin video izleme sürelerine göre otomatik belirlenir.
          </p>
        </div>
      </div>

      <div className="relative mb-6 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Öğrenci, eğitim veya sertifika no ara..."
          className="admin-input pl-9"
        />
      </div>

      <div className="border border-white/10 divide-y divide-white/10">
        {filtered.map((c) => (
          <div key={c.certificate_id} className="flex items-center justify-between gap-4 p-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-10 h-10 border border-gold-30 flex items-center justify-center shrink-0">
                <Award className="w-4 h-4 text-gold" />
              </div>
              <div className="min-w-0">
                <div className="text-white font-medium truncate">{c.student_name}</div>
                <div className="text-sm text-white/50 truncate">{c.course_title}</div>
                <div className="text-xs text-white/40 mt-0.5">{c.certificate_id}</div>
              </div>
            </div>
            <div className="flex items-center gap-6 shrink-0">
              <div className="text-sm text-white/60 hidden md:flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5" />
                {toDateInput(c.started_at)} → {toDateInput(c.issued_at)}
              </div>
              <button onClick={() => openEdit(c)} className="btn-outline text-xs py-1.5 px-3">
                Tarihi Düzenle
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="p-10 text-center text-white/40 text-sm">Sertifika bulunamadı.</div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-surface border border-white/10 p-8 max-w-md w-full">
            <h2 className="font-display text-xl mb-1">{editing.student_name}</h2>
            <p className="text-sm text-white/50 mb-6">{editing.course_title}</p>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-white/60 mb-2 inline-block">
                  Başlangıç Tarihi
                </span>
                <input
                  type="date"
                  value={form.started_at}
                  onChange={(e) => setForm((f) => ({ ...f, started_at: e.target.value }))}
                  className="admin-input"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-white/60 mb-2 inline-block">
                  Bitiş Tarihi
                </span>
                <input
                  type="date"
                  value={form.issued_at}
                  onChange={(e) => setForm((f) => ({ ...f, issued_at: e.target.value }))}
                  className="admin-input"
                />
              </label>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setEditing(null)} className="btn-outline flex-1 justify-center">
                Vazgeç
              </button>
              <button onClick={save} disabled={saving} className="btn-primary flex-1 justify-center disabled:opacity-60">
                <Save className="w-4 h-4" /> {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.admin-input { width: 100%; background: #0F0F14; border: 1px solid rgba(255,255,255,0.08); padding: 0.6rem 0.9rem; color: white; font-family: 'Outfit', sans-serif; outline: none; font-size: 0.875rem; } .admin-input:focus { border-color: rgba(212,175,55,0.6); }`}</style>
    </div>
  );
}
